import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Avatar } from '../ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { 
  Users, 
  TrendingUp, 
  Copy, 
  Star, 
  Eye, 
  DollarSign, 
  Award,
  Search,
  Filter,
  Heart,
  MessageCircle,
  Share2,
  BarChart3,
  Trophy
} from 'lucide-react';

interface Trader {
  id: string;
  name: string;
  avatar: string;
  level: number;
  roi: number;
  followers: number;
  copiers: number;
  totalTrades: number;
  winRate: number;
  risk: 'Low' | 'Medium' | 'High';
  verified: boolean;
  ndhHolder: boolean;
  recentTrades: Trade[];
}

interface Trade {
  id: string;
  token: string;
  action: 'buy' | 'sell';
  amount: number;
  price: number;
  pnl: number;
  timestamp: Date;
}

interface TradingPost {
  id: string;
  trader: Trader;
  content: string;
  image?: string;
  likes: number;
  comments: number;
  shares: number;
  timestamp: Date;
  tags: string[];
}

export function SocialTradingHub() {
  const [selectedTab, setSelectedTab] = useState('discover');
  const [searchQuery, setSearchQuery] = useState('');

  const topTraders: Trader[] = [
    {
      id: '1',
      name: 'CryptoKing_NDH',
      avatar: '👑',
      level: 89,
      roi: 247.8,
      followers: 15420,
      copiers: 1847,
      totalTrades: 2341,
      winRate: 78.5,
      risk: 'Medium',
      verified: true,
      ndhHolder: true,
      recentTrades: []
    },
    {
      id: '2',
      name: 'DefiMaster',
      avatar: '🚀',
      level: 76,
      roi: 189.2,
      followers: 8932,
      copiers: 1234,
      totalTrades: 1876,
      winRate: 82.1,
      risk: 'Low',
      verified: true,
      ndhHolder: true,
      recentTrades: []
    },
    {
      id: '3',
      name: 'YieldHunter',
      avatar: '💎',
      level: 65,
      roi: 156.7,
      followers: 6543,
      copiers: 987,
      totalTrades: 1543,
      winRate: 75.3,
      risk: 'High',
      verified: false,
      ndhHolder: true,
      recentTrades: []
    }
  ];

  const tradingPosts: TradingPost[] = [
    {
      id: '1',
      trader: topTraders[0],
      content: 'Just opened a large position in $ETH after breaking the resistance at $2,400. Target: $2,650. Stop loss: $2,350. #ETH #Breakout #NDHSignal',
      likes: 234,
      comments: 56,
      shares: 89,
      timestamp: new Date(Date.now() - 1000 * 60 * 15),
      tags: ['ETH', 'Breakout', 'NDHSignal']
    },
    {
      id: '2',
      trader: topTraders[1],
      content: 'Portfolio update: +15.7% this week thanks to DeFi plays. My copy traders are killing it! 🔥 Special thanks to NDH ecosystem for the alpha.',
      likes: 189,
      comments: 43,
      shares: 67,
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      tags: ['Portfolio', 'DeFi', 'NDH']
    },
    {
      id: '3',
      trader: topTraders[2],
      content: 'Market analysis: Expecting volatility in the next 48h. Reduced position sizes and increased cash allocation to 25%. Stay safe out there! #RiskManagement',
      likes: 145,
      comments: 78,
      shares: 34,
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6),
      tags: ['Analysis', 'RiskManagement']
    }
  ];

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'High': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2>Social Trading Hub</h2>
          <p className="text-muted-foreground">
            Follow, copy, and interact with top NDH traders worldwide
          </p>
        </div>
        <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
          NDH Exclusive
        </Badge>
      </div>

      <Tabs defaultValue="discover" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="discover">Discover</TabsTrigger>
          <TabsTrigger value="following">Following</TabsTrigger>
          <TabsTrigger value="feed">Feed</TabsTrigger>
          <TabsTrigger value="competitions">Competitions</TabsTrigger>
        </TabsList>

        <TabsContent value="discover" className="space-y-6">
          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search traders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </div>

          {/* Top Traders Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topTraders.map((trader) => (
              <Card key={trader.id} className="relative">
                {trader.ndhHolder && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                      NDH
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center">
                  <div className="w-16 h-16 mx-auto text-4xl rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                    {trader.avatar}
                  </div>
                  <div>
                    <CardTitle className="flex items-center justify-center gap-2">
                      {trader.name}
                      {trader.verified && <Award className="h-4 w-4 text-blue-500" />}
                    </CardTitle>
                    <CardDescription>Level {trader.level}</CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-500">+{trader.roi}%</p>
                      <p className="text-sm text-muted-foreground">Total ROI</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{trader.winRate}%</p>
                      <p className="text-sm text-muted-foreground">Win Rate</p>
                    </div>
                    <div>
                      <p className="text-lg font-medium">{trader.followers.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Followers</p>
                    </div>
                    <div>
                      <p className="text-lg font-medium">{trader.copiers.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Copiers</p>
                    </div>
                  </div>

                  {/* Risk Badge */}
                  <div className="flex justify-center">
                    <Badge className={getRiskColor(trader.risk)}>
                      {trader.risk} Risk
                    </Badge>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <Users className="h-4 w-4 mr-2" />
                      Follow
                    </Button>
                    <Button size="sm" className="bg-gradient-to-r from-green-500 to-emerald-500">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="feed" className="space-y-6">
          <div className="space-y-4">
            {tradingPosts.map((post) => (
              <Card key={post.id}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                      {post.trader.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{post.trader.name}</h4>
                        {post.trader.verified && <Award className="h-4 w-4 text-blue-500" />}
                        {post.trader.ndhHolder && (
                          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                            NDH
                          </Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          Level {post.trader.level}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatTime(post.timestamp)}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p>{post.content}</p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        #{tag}
                      </Badge>
                    ))}
                  </div>

                  {/* Engagement Stats */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-6">
                      <button className="flex items-center gap-2 text-muted-foreground hover:text-red-500 transition-colors">
                        <Heart className="h-4 w-4" />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <button className="flex items-center gap-2 text-muted-foreground hover:text-blue-500 transition-colors">
                        <MessageCircle className="h-4 w-4" />
                        <span className="text-sm">{post.comments}</span>
                      </button>
                      <button className="flex items-center gap-2 text-muted-foreground hover:text-green-500 transition-colors">
                        <Share2 className="h-4 w-4" />
                        <span className="text-sm">{post.shares}</span>
                      </button>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Trade
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="competitions" className="space-y-6">
          <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-6 w-6" />
                Monthly Trading Championship
              </CardTitle>
              <CardDescription className="text-orange-100">
                Compete for 10,000 NDH prize pool + exclusive NFTs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">127</p>
                  <p className="text-sm opacity-90">Participants</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">7 days</p>
                  <p className="text-sm opacity-90">Time left</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">10,000</p>
                  <p className="text-sm opacity-90">NDH Prize</p>
                </div>
              </div>
              <Button className="w-full bg-white text-orange-500 hover:bg-orange-50">
                Join Competition
              </Button>
            </CardContent>
          </Card>

          {/* Leaderboard */}
          <Card>
            <CardHeader>
              <CardTitle>Current Leaderboard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((rank) => (
                  <div key={rank} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-orange-500' : 'bg-gray-200'
                      } text-white font-bold text-sm`}>
                        {rank}
                      </div>
                      <div>
                        <p className="font-medium">Trader_{rank}234</p>
                        <p className="text-sm text-muted-foreground">Level {45 - rank}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-500">+{(25 - rank * 3).toFixed(1)}%</p>
                      <p className="text-sm text-muted-foreground">{150 - rank * 20} trades</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}